// Formula configuration types and defaults

export interface Formula {
  name: string
  formula: string
  description: string
  category: 'key-metric' | 'profitability' | 'assessment'
  isHighlight?: boolean
}

export interface AuditLogEntry {
  id: string
  timestamp: string
  userEmail: string
  userName: string
  formulaKey: string
  formulaName: string
  action: 'modified' | 'reset' | 'reset_all'
  oldValue?: string
  newValue?: string
}

export const DEFAULT_FORMULAS: Record<string, Formula> = {
  arr: {
    name: 'ARR (Annual Recurring Revenue)',
    formula: 'annualPlatformFees + transactionRevenue',
    description: 'Annual Recurring Revenue combines platform fees with estimated transaction revenue.',
    category: 'key-metric',
  },
  mrr: {
    name: 'MRR (Monthly Recurring Revenue)',
    formula: 'arr / 12',
    description: 'Monthly Recurring Revenue is ARR divided by 12 months.',
    category: 'key-metric',
  },
  committedArr: {
    name: 'Committed ARR',
    formula: 'minimumCommitment + annualPlatformFees',
    description: 'Committed ARR is the guaranteed recurring revenue from minimum commitments plus platform fees.',
    category: 'key-metric',
  },
  totalRevenue: {
    name: 'Total Revenue Year 1',
    formula: 'annualPlatformFees + implementationFee + implementationSupport + max(transactionRevenue, minimumCommitment)',
    description: 'Total first year revenue including all fees and the greater of transaction revenue or minimum commitment.',
    category: 'key-metric',
  },
  tcv: {
    name: 'TCV (Total Contract Value)',
    formula: 'max((mrr * contractTermMonths) + platformFees, tcvCommitted)',
    description: 'Total Contract Value is the maximum of projected revenue over the contract term or committed TCV.',
    category: 'key-metric',
    isHighlight: true,
  },
  tcvCommitted: {
    name: 'TCV Committed',
    formula: 'platformFeesOverTerm + minimumCommitmentOverTerm + implementationFee + implementationSupport',
    description: 'Committed TCV is the guaranteed contract value based on committed fees over the contract term.',
    category: 'key-metric',
  },
  grossProfit: {
    name: 'Gross Profit',
    formula: 'tcv - totalCosts',
    description: 'Gross Profit is TCV minus total costs (transaction costs over contract term).',
    category: 'profitability',
  },
  grossMargin: {
    name: 'Gross Margin %',
    formula: 'tcv > 0 ? (grossProfit / tcv * 100) : 0',
    description: 'Gross Margin percentage is gross profit divided by TCV, expressed as a percentage.',
    category: 'profitability',
    isHighlight: true,
  },
  cac: {
    name: 'CAC (Customer Acquisition Cost)',
    formula: 'baseCac - ((0.11 + partnerCommissionRate) * totalRevenueYr1)',
    description: 'CAC is the base acquisition cost adjusted for commission rates on first year revenue.',
    category: 'profitability',
  },
  ltv: {
    name: 'LTV (Lifetime Value)',
    formula: 'grossProfit * 1.5',
    description: 'Customer Lifetime Value is calculated as gross profit multiplied by a lifetime factor.',
    category: 'profitability',
  },
  ltvCac: {
    name: 'LTV/CAC Ratio',
    formula: 'cac !== 0 ? ltv / abs(cac) : 0',
    description: 'The ratio of customer lifetime value to acquisition cost. Higher is better (3x+ is good).',
    category: 'profitability',
    isHighlight: true,
  },
  payback: {
    name: 'Payback Period',
    formula: 'monthlyGrossProfit > 0 ? abs(cac) / monthlyGrossProfit : 0',
    description: 'Months to recoup customer acquisition cost from monthly gross profit.',
    category: 'profitability',
    isHighlight: true,
  },
  pc3: {
    name: 'PC3 (Profitability Calculation)',
    formula: '(platformFees + arr * ltvMultiplier * (grossProfitMargin / 100)) + cac + onboarding.yr1 + (onboarding.ongoing * (ltvMultiplier - 1)) + (hosting * ltvMultiplier)',
    description: 'PC3 is a comprehensive profitability calculation including platform fees, CAC, onboarding, and hosting costs over the customer lifetime.',
    category: 'profitability',
  },
  dealAssessment: {
    name: 'Deal Assessment Score',
    formula: 'tcvRank * tcvCommittedRank * marginRank * ltvCacRank * ltvCacMinCommitRank * paybackRank * ltvCacCheck',
    description: 'Overall score multiplying ranks from TCV, TCV Committed, Margin, LTV/CAC, LTV/CAC at Min Commit, Payback, and LTV/CAC Check. Great ≥252, Good ≥140, Average ≥60, Poor <60.',
    category: 'assessment',
    isHighlight: true,
  },
}
